// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import TextField, { TextFieldProps } from "@flip/MobiusOverrides/TextField";
import Search from "@flip/XDL/Icons/Search";
import { fontConfig, headingsFontConfig } from "@flip/XDL/PreStyle/typography";
import { color, xScreen } from "@flip/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Link, { LinkProps, StyledTypography } from "@insite/mobius/Link";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { css } from "styled-components";

type Props = {
    alphabet: LetterDetails[];
    prefix?: string;
    extendedStyles?: AlphabetNavigationStyles;
    onLetterClick?: (letter: string) => void;
    // XNG-Changes
    handleSearchFilterBrands?: (e: string) => void;
    searchFilterBrands?: string;
    selectedLetter?: string;
};

export interface LetterDetails {
    letter: string;
    linkable: boolean;
}

export interface AlphabetNavigationStyles {
    container?: InjectableCss;
    navigationItem?: InjectableCss;
    navigationLink?: LinkProps;
    navigationLinkText?: TypographyPresentationProps;
    navigationTextEmpty?: TypographyPresentationProps;
    // XNG-Changes
    titleText?: TypographyPresentationProps;
    brandsLettersWrapper?: InjectableCss;
    alphabetSection?: InjectableCss;
    filterSection?: InjectableCss;
    filterInput?: TextFieldProps;
    selectWrapper?: InjectableCss;
    select?: SelectPresentationProps;
}

export const alphabetNavigationStyles: AlphabetNavigationStyles = {
    container: {
        css: css`
            display: flex;
            background-color: ${color("common.ui16")};
            padding: 24px;
            margin: 32px 0;
            column-gap: 24px;
            justify-content: space-between;

            ${xScreen("lte", "md")} {
                margin-bottom: 56px;
            }

            ${xScreen("lt", "sm")} {
                margin-bottom: 48px;
                flex-direction: column;
            }
        `,
    },
    titleText: {
        variant: "h5",
        forwardAs: "h2",
        css: css`
            margin-bottom: 4px;
            width: 100%;
        `,
    },
    brandsLettersWrapper: {
        css: css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            text-transform: capitalize;
            flex-wrap: wrap;

            ${xScreen("lte", "md")} {
                width: 100%;
            }
        `,
    },
    navigationItem: {
        css: css`
            display: inline-flex;
            height: 21px;
            width: 21px;

            ${({ selectedLetter, enabled }: any) =>
                selectedLetter &&
                enabled &&
                css`
                    ${TypographyStyle} {
                        font-weight: 500;
                        text-decoration: underline;
                        &:hover {
                            text-decoration: underline;
                        }
                    }
                `}
        `,
    },
    navigationLink: {
        css: css`
            &:focus {
                outline: none;
            }

            ${StyledTypography} {
                color: ${color("text")};
            }
        `,
        typographyProps: {
            transform: "capitalize",
            forwardAs: "span",
            size: 16,
            lineHeight: "26px",
            weight: fontConfig.weight.semiBold,
            css: css`
                text-align: center;
                display: block;
                letter-spacing: 0;
                width: 21px;
                height: 100%;
            `,
        },
    },
    navigationTextEmpty: {
        forwardAs: "span",
        color: "text.disabled",
        lineHeight: "26px",
        weight: "bold",
        css: css`
            text-align: center;
            display: block;
            letter-spacing: 0;
            width: 21px;
            opacity: 0.66;
            cursor: not-allowed;
        `,
    },
    alphabetSection: {
        css: css`
            display: flex;
            flex-wrap: wrap;

            ${xScreen("lte", "md")} {
                display: none;
            }
        `,
    },
    filterSection: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            width: 100%;
        `,
    },
    filterInput: {
        cssOverrides: {
            formField: css`
                width: 500px;

                ${xScreen("lt", "lg")} {
                    width: 300px;
                }

                ${xScreen("lte", "md")} {
                    width: 100%;
                }
            `,
        },
        iconProps: { src: Search, color: "brand-01", size: 16 },
        labelProps: {
            size: 18,
            lineHeight: "22px",
            fontFamily: headingsFontConfig.fontFamily,
            weight: headingsFontConfig.weight.bold,
            css: css`
                margin-bottom: 4px;
            `,
        },
    },
    selectWrapper: {
        css: css`
            display: none;
            ${xScreen("lte", "md")} {
                width: 100%;
                display: block;
            }
        `,
    },
    select: {
        cssOverrides: {
            formInputWrapper: css`
                &&& {
                    margin-top: 5px;
                    width: 100%;
                }

                ${xScreen("lt", "sm")} {
                    margin-bottom: 20px;
                }
            `,
        },
        labelProps: {
            variant: "h6",
            css: css`
                min-height: 19px;
                margin-bottom: 0;
                ${xScreen("gte", 1025)} {
                    display: none;
                }
            `,
        },
    },
};

const StyledSpan = getStyledWrapper("span");

const AlphabetNavigation: FC<Props> = (props: Props) => {
    const [styles] = useState(() => mergeToNew(alphabetNavigationStyles, props.extendedStyles));
    const [alphabet] = useState(() => [{ letter: "all", count: 0, linkable: true }, ...props.alphabet]);
    const [selectedLetter, setSelectedLetter] = useState(props.selectedLetter);

    // XNG-Changes: To get selectedLetter change when a user click on clean Filter
    useEffect(() => {
        setSelectedLetter(props.selectedLetter);
    }, [props.selectedLetter]);

    const handleClickOfLetter = (letter: string) => {
        props.onLetterClick?.(letter);
    };

    // XNG-Changes
    const createHandleClickOfLetter = (letter: string) => () => {
        setSelectedLetter(letter);
        if (props.searchFilterBrands && props.handleSearchFilterBrands) {
            props.handleSearchFilterBrands(props.searchFilterBrands);
        }
        return handleClickOfLetter(letter);
    };

    // XNG-Changes: we add `All` as a first element
    if (alphabet.length <= 1) {
        return null;
    }

    // XNG-Changes
    const handleSelectChange = (e: any) => {
        setSelectedLetter(e.target.value);
        return handleClickOfLetter(e.target.value);
    };

    return (
        <StyledWrapper {...styles.container} data-test-selector="alphabetNavigation">
            <StyledWrapper {...styles.brandsLettersWrapper}>
                <Typography {...styles.titleText}>{translate("Filter Alphabetically")}</Typography>
                <StyledWrapper {...styles.alphabetSection}>
                    {alphabet.map((alphabetItem, index) => (
                        <StyledSpan
                            {...styles.navigationItem}
                            {...{
                                selectedLetter: selectedLetter === alphabetItem.letter && alphabetItem.linkable,
                                enabled: alphabetItem.linkable,
                            }}
                            key={alphabetItem.letter}
                            data-test-selector="alphabetNavigationItemLetter"
                        >
                            {alphabetItem.linkable ? (
                                <Link
                                    {...styles.navigationLink}
                                    onClick={createHandleClickOfLetter(alphabetItem.letter)}
                                    aria-label={alphabetItem.letter}
                                >
                                    {alphabetItem.letter}
                                </Link>
                            ) : (
                                <Typography {...styles.navigationTextEmpty}>{alphabetItem.letter}</Typography>
                            )}
                        </StyledSpan>
                    ))}
                </StyledWrapper>
                <StyledWrapper {...styles.selectWrapper}>
                    <Select
                        {...styles.select}
                        placeholder={translate("Search by First Letter")}
                        onChange={e => handleSelectChange(e)}
                        value={"" || selectedLetter}
                        label=""
                    >
                        <option disabled value="">
                            {translate("Search by First Letter")}
                        </option>
                        {alphabet.map((alphabetItem, index) => (
                            <>
                                {alphabetItem.linkable ? (
                                    <option key={alphabetItem.letter} value={alphabetItem.letter}>
                                        {alphabetItem.letter === "#"
                                            ? translate("0-9")
                                            : alphabetItem.letter.toUpperCase()}
                                    </option>
                                ) : (
                                    <option disabled key={alphabetItem.letter} value={alphabetItem.letter}>
                                        {alphabetItem.letter.toUpperCase()}
                                    </option>
                                )}
                            </>
                        ))}
                    </Select>
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.brandsLettersWrapper}>
                <StyledWrapper {...styles.filterSection}>
                    <TextField
                        name="filterBrands"
                        value={props.searchFilterBrands}
                        placeholder={translate("Search by Keyword")}
                        onChange={e => props.handleSearchFilterBrands && props.handleSearchFilterBrands(e.target.value)}
                        {...styles.filterInput}
                        iconClickableProps={{
                            iconPosition: "left",
                        }}
                        label={translate("Search by Name")}
                    />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default AlphabetNavigation;
